// Smart View speichern und bearbeiten (Agent 5A, Runde 5): „Speichern unter“ (neuer Name, Emoji, Freigabe) und „Smart View bearbeiten“
// (Name, Emoji, Freigabe ändern, löschen). Beides gegen `smartViewsSchreibenApi` (API.md 20.1). Die Dialoge sind eigenständig und
// dürfen von anderen Seiten genutzt werden, z. B. dem „…“-Menü der Smart-View-Übersicht (5B).
import { useEffect, useRef, useState } from "react";
import { fehlerText } from "../../api";
import { smartViewsSchreibenApi } from "../../api/filter";
import type { SmartView } from "../../api/types";
import { Ausklapp } from "../Ausklapp";
import { Dialog } from "../Dialog";
import { Icon } from "../Icon";
import { EMOJI_AUSWAHL, namenBauen, namenTeilen } from "./filterName";
import { FILTER_TEXTE } from "./filterTexte";

const T = FILTER_TEXTE.speichern;

function EmojiWahl({ emoji, onWahl }: { emoji: string; onWahl: (e: string) => void }) {
  const [anker, setAnker] = useState<HTMLButtonElement | null>(null);
  const [offen, setOffen] = useState(false);
  return (
    <>
      <button ref={setAnker} type="button" className="fsp__emoji" aria-label={T.emoji} aria-haspopup="dialog" aria-expanded={offen} title={T.emoji} onClick={() => setOffen((o) => !o)}>
        {emoji || <Icon name="ansicht" groesse={16} />}
      </button>
      <Ausklapp anker={anker} offen={offen} onSchliessen={() => setOffen(false)} breite={284} rolle="dialog" className="fsp-ausklapp">
        <div className="fsp__emojis" role="listbox" aria-label={T.emoji}>
          <button type="button" className="fsp__emojikein" onClick={() => (onWahl(""), setOffen(false))}>
            {T.emojiKein}
          </button>
          {EMOJI_AUSWAHL.map((e) => (
            <button key={e} type="button" role="option" aria-selected={e === emoji} className={`fsp__emojiwahl${e === emoji ? " fsp__emojiwahl--an" : ""}`} onClick={() => (onWahl(e), setOffen(false))}>
              {e}
            </button>
          ))}
        </div>
      </Ausklapp>
    </>
  );
}

function NameFeld({ emoji, text, onEmoji, onText, onEnter }: { emoji: string; text: string; onEmoji: (e: string) => void; onText: (t: string) => void; onEnter: () => void }) {
  return (
    <div className="fsp__name">
      <EmojiWahl emoji={emoji} onWahl={onEmoji} />
      <input
        className="eingabe"
        value={text}
        placeholder={T.namePlatzhalter}
        aria-label={T.name}
        maxLength={200}
        data-autofocus
        onChange={(e) => onText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onEnter();
          }
        }}
      />
    </div>
  );
}

function TeilenHaken({ geteilt, onAendern, darf }: { geteilt: boolean; onAendern: (g: boolean) => void; darf: boolean }) {
  return (
    <label className={`fsp__teilen${darf ? "" : " fsp__teilen--aus"}`} title={darf ? T.teilenHinweis : T.teilenKeinRecht}>
      <input type="checkbox" checked={geteilt} disabled={!darf} onChange={(e) => onAendern(e.target.checked)} />
      <span>
        {T.teilen}
        <span className="fsp__hinweis">{darf ? T.teilenHinweis : T.teilenKeinRecht}</span>
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Speichern unter
// ---------------------------------------------------------------------------------------------------------------------------------

interface UnterProps {
  offen: boolean;
  onSchliessen: () => void;
  /** vorgeschlagener Name (mit Emoji) */
  vorschlag: string;
  darfTeilen: boolean;
  /** Smart View, die kopiert wird (leer auf der Leads-Liste) */
  kopieVon?: SmartView | null;
  /** speichert und liefert die neue Smart View; der Dialog zeigt Fehler und schließt bei Erfolg */
  onSpeichern: (name: string, geteilt: boolean) => Promise<void>;
}

export function SpeichernUnterDialog({ offen, onSchliessen, vorschlag, darfTeilen, kopieVon, onSpeichern }: UnterProps) {
  const [emoji, setEmoji] = useState("");
  const [text, setText] = useState("");
  const [geteilt, setGeteilt] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!offen) return;
    const t = namenTeilen(vorschlag);
    setEmoji(t.emoji);
    setText(t.text);
    setGeteilt(false);
    setFehler(null);
    setLaedt(false);
  }, [offen, vorschlag]);

  const speichern = async () => {
    if (laedt) return;
    if (!text.trim()) {
      setFehler(T.nameFehlt);
      return;
    }
    setLaedt(true);
    setFehler(null);
    try {
      await onSpeichern(namenBauen(emoji, text), geteilt && darfTeilen);
      onSchliessen();
    } catch (e) {
      setFehler(fehlerText(e));
      setLaedt(false);
    }
  };

  return (
    <Dialog
      titel={T.speichernUnterTitel}
      offen={offen}
      onSchliessen={onSchliessen}
      breite={460}
      fuss={
        <>
          <button type="button" className="knopf knopf--leise" onClick={onSchliessen}>
            {T.abbrechen}
          </button>
          <button type="button" className="knopf knopf--primaer" disabled={laedt} onClick={() => void speichern()}>
            {laedt ? "Speichert …" : T.speichernUnterKnopf}
          </button>
        </>
      }
    >
      <div className="fsp">
        <label className="fsp__label">{T.name}</label>
        <NameFeld emoji={emoji} text={text} onEmoji={setEmoji} onText={setText} onEnter={() => void speichern()} />
        <TeilenHaken geteilt={geteilt} onAendern={setGeteilt} darf={darfTeilen} />
        {kopieVon?.source === "close" ? <p className="fsp__hinweis">Die Kopie entsteht im CRM, „{kopieVon.name}“ bleibt unverändert.</p> : null}
        {fehler ? (
          <p className="fsp__fehler" role="alert">
            {fehler}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------------------------------------
// Smart View bearbeiten
// ---------------------------------------------------------------------------------------------------------------------------------

interface BearbeitenProps {
  offen: boolean;
  onSchliessen: () => void;
  view: SmartView | null;
  /** nach dem Speichern: die geänderte Smart View (bei einer Close-Smart-View ist es die neue CRM-Kopie mit anderer ID) */
  onGespeichert: (view: SmartView) => void;
  /** nach dem Löschen */
  onGeloescht: (view: SmartView) => void;
  darfTeilen: boolean;
  /** Menüpunkt „Smart View löschen“: die Rückfrage steht schon da, ein Klick löscht */
  loeschenGleich?: boolean;
}

export function SmartViewBearbeitenDialog({ offen, onSchliessen, view, onGespeichert, onGeloescht, darfTeilen, loeschenGleich = false }: BearbeitenProps) {
  const [emoji, setEmoji] = useState("");
  const [text, setText] = useState("");
  const [geteilt, setGeteilt] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [loeschenFrage, setLoeschenFrage] = useState(false);
  const loeschenTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!offen || !view) return;
    const t = namenTeilen(view.name);
    setEmoji(t.emoji);
    setText(t.text);
    setGeteilt(view.is_shared);
    setFehler(null);
    setLaedt(false);
    setLoeschenFrage(loeschenGleich);
  }, [offen, view, loeschenGleich]);
  useEffect(() => () => window.clearTimeout(loeschenTimer.current), []);

  if (!view) return null;
  const kann = view.kann ?? { bearbeiten: true, loeschen: true, teilen: darfTeilen };
  const neuerName = namenBauen(emoji, text);
  const geaendert = neuerName !== view.name || geteilt !== view.is_shared;

  const speichern = async () => {
    if (laedt || !geaendert) return;
    if (!text.trim()) {
      setFehler(T.nameFehlt);
      return;
    }
    setLaedt(true);
    setFehler(null);
    try {
      const r = await smartViewsSchreibenApi.aendern(view.id, {
        ...(neuerName !== view.name ? { name: neuerName } : {}),
        ...(geteilt !== view.is_shared ? { is_shared: geteilt } : {}),
      });
      onGespeichert(r.smart_view);
      onSchliessen();
    } catch (e) {
      setFehler(fehlerText(e));
      setLaedt(false);
    }
  };

  const loeschen = async () => {
    if (!loeschenFrage) {
      setLoeschenFrage(true);
      window.clearTimeout(loeschenTimer.current);
      loeschenTimer.current = window.setTimeout(() => setLoeschenFrage(false), 5000);
      return;
    }
    setLaedt(true);
    setFehler(null);
    try {
      await smartViewsSchreibenApi.loeschen(view.id);
      onGeloescht(view);
      onSchliessen();
    } catch (e) {
      setFehler(fehlerText(e));
      setLaedt(false);
      setLoeschenFrage(false);
    }
  };

  return (
    <Dialog
      titel={T.bearbeiten}
      offen={offen}
      onSchliessen={onSchliessen}
      breite={460}
      fuss={
        <>
          {kann.loeschen ? (
            <button type="button" className={`knopf ${loeschenFrage ? "knopf--gefahr" : "knopf--leise"} fsp__loeschen`} disabled={laedt} onClick={() => void loeschen()}>
              <Icon name="papierkorb" groesse={14} />
              {loeschenFrage ? T.loeschenFrage : T.loeschen}
            </button>
          ) : null}
          <button type="button" className="knopf knopf--leise" onClick={onSchliessen}>
            {T.abbrechen}
          </button>
          <button type="button" className="knopf knopf--primaer" disabled={laedt || !geaendert || !kann.bearbeiten} onClick={() => void speichern()}>
            {laedt ? "Speichert …" : T.speichern}
          </button>
        </>
      }
    >
      <div className="fsp">
        {!kann.bearbeiten ? <p className="fsp__fehler">{T.keinRecht}</p> : null}
        <label className="fsp__label">{T.name}</label>
        <NameFeld emoji={emoji} text={text} onEmoji={setEmoji} onText={setText} onEnter={() => void speichern()} />
        <TeilenHaken geteilt={geteilt} onAendern={setGeteilt} darf={kann.teilen} />
        {view.source === "close" ? <p className="fsp__hinweis">{T.closeHinweis}</p> : null}
        {fehler ? (
          <p className="fsp__fehler" role="alert">
            {fehler}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
